import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '@/api/apiBase';
import { archiveTripAlbum, removeAlbumCollaborator, removePhotosFromAlbum } from '@/api/immich';
import { applyOp, emptyClocks, type Op, type TripClocks } from '@/collab/ops';
import { type HydratedTrip, tripStateToTrip } from '@/collab/tripMapping';
import { createTripSync as createTripSyncDefault, type TripSyncClient } from '@/collab/tripSync';
import { useSession } from '@/lib/authClient';
import type { ItineraryItem, PlacePhoto } from '@/types/itinerary';
import type {
  EnRouteDirection,
  ImmichPhotoRef,
  Trip,
  TripCollaborator,
  TripPlace,
  TripPlaceType,
} from '@/types/trip';

// Server-sourced photos carry a `provider` discriminator. Legacy localStorage
// photos (pre-migration Google, no discriminator) can no longer be rendered —
// the Google photo path is gone — so drop them on read.
const isRenderablePhoto = (raw: PlacePhoto | { name?: string }): raw is PlacePhoto =>
  'provider' in raw;

const backfillItem = (item: ItineraryItem): ItineraryItem => ({
  ...item,
  photos: (item.photos ?? []).filter(isRenderablePhoto),
});

const STORAGE_KEY = 'trip-explorer:trips';
// Which account the cached trips belong to — so a shared device doesn't show a
// previous user's trips after someone else logs in.
const TRIPS_USER_KEY = 'trip-explorer:trips-user';
// Trip ids the server listed on the last fetch. Lets reconciliation tell a
// server-confirmed-but-now-gone trip (deleted/unshared elsewhere → drop) from a
// never-confirmed one (created offline, not yet synced → keep).
const KNOWN_SERVER_TRIPS_KEY = 'trip-explorer:known-server-trips';

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const loadKnownServerTrips = (): Array<string> => {
  try {
    return JSON.parse(safeGet(KNOWN_SERVER_TRIPS_KEY) ?? '[]') as Array<string>;
  } catch {
    return [];
  }
};

const saveKnownServerTrips = (ids: Array<string>): void => {
  try {
    localStorage.setItem(KNOWN_SERVER_TRIPS_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage unavailable */
  }
};

/** Summary shape returned by GET /api/trips. */
interface ServerTripSummary {
  id: string;
  name: string | null;
  location: string | null;
  startDate: string | null;
  endDate: string | null;
  role: 'owner' | 'collaborator';
}

const today = (): string => new Date().toISOString().slice(0, 10);

const backfillTrip = (raw: Trip): Trip => {
  return {
    ...raw,
    // Provide defaults for fields added after the initial release
    startDate: raw.startDate || today(),
    endDate: raw.endDate || today(),
    enRoute: raw.enRoute ?? [],
    immichPhotos: raw.immichPhotos ?? [],
    deletedPlaceIds: {},
    places: raw.places.map(
      (p): TripPlace => ({
        ...p,
        item: backfillItem(p.item),
        placeType: p.placeType || ('activity' as TripPlaceType),
        scheduledDay: p.scheduledDay ?? null,
        scheduledDayEnd: p.scheduledDayEnd ?? null,
        scheduledTime: p.scheduledTime ?? null,
        estimatedDurationMinutes: p.estimatedDurationMinutes ?? null,
        checkInTime: p.checkInTime ?? null,
        checkInEndTime: p.checkInEndTime ?? null,
        checkOutTime: p.checkOutTime ?? null,
        checkOutEndTime: p.checkOutEndTime ?? null,
        immichPhotos: p.immichPhotos ?? [],
      }),
    ),
  };
};

/** A minimal Trip for a server-listed trip we don't have cached — the per-trip
 *  sync connection hydrates its full state (places, etc.) on connect. */
const stubFromSummary = (summary: ServerTripSummary): Trip =>
  backfillTrip({
    id: summary.id,
    name: summary.name ?? 'My Trip',
    location: summary.location ?? '',
    startDate: summary.startDate ?? today(),
    endDate: summary.endDate ?? today(),
    createdAt: 0,
    places: [],
  } as Trip);

const loadFromStorage = (): Array<Trip> => {
  // One-time cleanup: purge orphaned localStorage keys from older versions of the app
  // that cached search results client-side. These can take up megabytes and starve our
  // trip storage of space.
  purgeOrphanedKeys();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return (JSON.parse(raw) as Array<Trip>).map(backfillTrip);
  } catch {
    return [];
  }
};

/** Remove orphaned cache keys that older app versions left behind. */
const purgeOrphanedKeys = (): void => {
  try {
    const orphans: Array<string> = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('trip-explorer:places:') ||
          key === 'trip-explorer:exhausted-keys' ||
          key === 'trip-explorer:exhaustive-search')
      ) {
        orphans.push(key);
      }
    }
    for (const key of orphans) localStorage.removeItem(key);
  } catch {
    // localStorage unavailable — nothing to do
  }
};

const persistToStorage = (trips: Array<Trip>): void => {
  const json = JSON.stringify(trips);
  try {
    localStorage.setItem(STORAGE_KEY, json);
  } catch (err) {
    // Most common cause: QuotaExceededError. Free up space by purging orphaned cache keys
    // and retry once. If the retry still fails, surface a console error so the user knows.
    if (err instanceof DOMException && err.name === 'QuotaExceededError') {
      purgeOrphanedKeys();
      try {
        localStorage.setItem(STORAGE_KEY, json);
        return;
      } catch (retryErr) {
        console.error('[trip-explorer] localStorage write failed even after cleanup', retryErr);
        return;
      }
    }
    console.error('[trip-explorer] localStorage write failed', err);
  }
};

export interface UseTripsOptions {
  /** Test-only seam — inject a fake trip-sync factory instead of opening a
   *  real WebSocket per trip. Defaults to the real `createTripSync`, which
   *  is what every production caller gets. Mirrors the `makeSocket` seam
   *  `createTripSync` itself accepts, one layer up. */
  createTripSync?: typeof createTripSyncDefault;
}

export interface UseTripsResult {
  trips: Array<Trip>;
  createTrip: (
    name: string,
    location: string,
    startDate: string,
    endDate: string,
    lat?: number | null,
    lng?: number | null,
    radius?: number | null,
  ) => Trip;
  deleteTrip: (id: string) => void;
  addPlace: (tripId: string, item: ItineraryItem, placeType: TripPlaceType) => void;
  removePlace: (tripId: string, placeId: string) => void;
  schedulePlace: (
    tripId: string,
    placeId: string,
    day: number,
    dayEnd?: number | null,
    time?: string | null,
  ) => void;
  /** Atomic update of schedule-related fields. Used by the schedule form to
   * save day/time, estimated duration, and lodging check-in/out ranges in one
   * commit. Any field omitted from `update` is left unchanged; passing `null`
   * explicitly clears it. */
  updatePlaceSchedule: (
    tripId: string,
    placeId: string,
    update: Pick<
      TripPlace,
      | 'scheduledDay'
      | 'scheduledDayEnd'
      | 'scheduledTime'
      | 'estimatedDurationMinutes'
      | 'checkInTime'
      | 'checkInEndTime'
      | 'checkOutTime'
      | 'checkOutEndTime'
    >,
  ) => void;
  unschedulePlace: (tripId: string, placeId: string) => void;
  updatePlaceType: (tripId: string, placeId: string, placeType: TripPlaceType) => void;
  updatePlaceNote: (tripId: string, placeId: string, note: string | null) => void;
  isInTrip: (tripId: string, placeId: string) => boolean;
  isInAnyTrip: (placeId: string) => boolean;
  exportTrip: (tripId: string) => void;
  importTrip: (data: unknown) => Trip | null;
  updateTripLocation: (tripId: string, location: string, lat: number, lng: number) => void;
  updateTripDates: (tripId: string, startDate: string, endDate: string) => void;
  addEnRouteNote: (tripId: string, text: string, direction: EnRouteDirection) => void;
  updateEnRouteNote: (tripId: string, noteId: string, text: string) => void;
  removeEnRouteNote: (tripId: string, noteId: string) => void;
  /** Import a trip via a `?live=<id>` URL. Fetches the
   *  materialized trip and lands a local copy, which then connects via the
   *  same per-trip sync effect every other trip uses. Returns the trip, or
   *  null if the server has no record of that id. */
  importLiveSyncedTrip: (syncId: string) => Promise<Trip | null>;
  /** Persist a freshly-uploaded photo ref onto a place. Caller is
   *  expected to have already uploaded the bytes via uploadPhotoToImmich
   *  and to be passing the assetId from the worker response. */
  attachPhotoToPlace: (tripId: string, placeId: string, photo: ImmichPhotoRef) => void;
  /** Drop a photo from a place. Also fires a fire-and-forget worker call
   *  to remove the asset from the trip's Immich album (the underlying
   *  asset stays in the user's library). */
  removePhotoFromPlace: (tripId: string, placeId: string, assetId: string) => void;
  /** Persist a freshly-uploaded photo ref onto the trip overall (no
   *  associated place). Stored under `Trip.immichPhotos`. */
  attachPhotoToTrip: (tripId: string, photo: ImmichPhotoRef) => void;
  /** Drop a trip-level photo. Also fires a fire-and-forget worker call
   *  to remove the asset from the trip's Immich album. */
  removePhotoFromTrip: (tripId: string, assetId: string) => void;
  /** Persist the Immich album ID after the worker lazily creates an
   *  album on first photo upload. */
  setTripImmichAlbumId: (tripId: string, albumId: string) => void;
  /** Add a collaborator to a trip by email — this grants trip view/edit
   *  access (keyed on email via `authorizeTrip`). If the collaborator has an
   *  Immich account, pass `immichUserId` too so their photos sync via the
   *  album. Re-adding an existing email is a no-op. */
  addCollaborator: (tripId: string, collaborator: TripCollaborator) => void;
  /** Remove a collaborator by email. Best-effort revokes their Immich album
   *  access too (if they had one). */
  removeCollaborator: (tripId: string, email: string) => void;
}

export const useTrips = (options: UseTripsOptions = {}): UseTripsResult => {
  const createSync = options.createTripSync ?? createTripSyncDefault;

  // The signed-in account (empty in tests / before the session resolves). Trips
  // are owned by this account; the cache is keyed to it.
  const userId = useSession().data?.user.id ?? '';

  const [trips, setTrips] = useState<Array<Trip>>(() =>
    // Don't surface a previous user's cached trips on a shared device. With no
    // session (tests) keep the plain cache behavior.
    userId && safeGet(TRIPS_USER_KEY) !== userId ? [] : loadFromStorage(),
  );

  // Ref tracks the latest trips array so handlers that need to *read* trips
  // synchronously (mutators computing the next op batch, exportTrip, …) always
  // see the freshest state, even across several mutator calls issued
  // back-to-back within the same tick.
  const tripsRef = useRef(trips);
  tripsRef.current = trips;

  // Per-trip LWW clock sidecar — the client twin of the server's
  // field_clocks/added_clock/deleted_clock bookkeeping (see collab/ops.ts).
  // Not part of `Trip` itself and never exposed publicly; it's purely
  // bookkeeping so optimistic local edits and incoming op broadcasts
  // resolve last-write-wins the same way the server does.
  const clocksRef = useRef<Record<string, TripClocks>>({});

  // Per-tab collab identity, stamped onto every op this device sends.
  const clientIdRef = useRef<string>(crypto.randomUUID());

  // Monotonic per-device clock. `applyOp`'s LWW compare is strict
  // (`clock <= existing` loses), so two ops issued within the same
  // wall-clock millisecond — e.g. addPlace immediately followed by
  // removePlace in the same handler — would otherwise tie and the second
  // op would silently no-op. Falls back to wall time whenever it has
  // advanced past the last issued clock, so clocks stay meaningfully
  // comparable with peers' wall-clock-based clocks.
  const lastClockRef = useRef(0);
  const nextClock = useCallback((): number => {
    const wallClock = Date.now();
    const clock = wallClock > lastClockRef.current ? wallClock : lastClockRef.current + 1;
    lastClockRef.current = clock;
    return clock;
  }, []);

  const makeOp = useCallback(
    (
      tripId: string,
      entity: Op['entity'],
      type: Op['type'],
      overrides: Partial<Pick<Op, 'entityId' | 'field' | 'value'>> = {},
    ): Op => ({
      tripId,
      actorId: clientIdRef.current,
      clock: nextClock(),
      entity,
      type,
      ...overrides,
    }),
    [nextClock],
  );

  /** Local-only commit: update `trips` state + the ref + the localStorage
   *  cache (which now serves purely as an offline-first cache — the server
   *  is the source of truth). Used both by op dispatch below and by
   *  mutators that don't go through the op pipeline (deleteTrip, importTrip,
   *  the live-sync hydrate/op handlers, …). */
  const commitTrips = useCallback((updater: (prev: Array<Trip>) => Array<Trip>): void => {
    const next = updater(tripsRef.current);
    tripsRef.current = next;
    persistToStorage(next);
    setTrips(next);
  }, []);

  // Per-trip sync connections, keyed by trip id.
  const syncClientsRef = useRef<Map<string, TripSyncClient>>(new Map());

  /** Open (if not already open) the sync connection for one trip. Called
   *  both from the trips-driven effect below (covers trips restored from
   *  the localStorage cache on mount, and incoming trips generally) and
   *  synchronously from any mutator that adds a brand-new trip to state
   *  (createTrip, importTrip, importLiveSyncedTrip) — those need the
   *  client to exist *before* they dispatch the trip's first ops, which
   *  happens in the same synchronous call, well before the effect would
   *  otherwise get a chance to run. Idempotent. */
  const ensureSyncClient = useCallback(
    (tripId: string): TripSyncClient => {
      const existing = syncClientsRef.current.get(tripId);
      if (existing) return existing;
      const client = createSync(tripId, {
        onHydrate: (hydrated) => {
          // No server state yet (brand-new trip) → keep the local optimistic
          // trip; its create op will populate the server shortly.
          if (!hydrated) return;
          const mapped: Trip = tripStateToTrip(hydrated as HydratedTrip);
          clocksRef.current = { ...clocksRef.current, [tripId]: emptyClocks() };
          commitTrips((prev) => prev.map((t) => (t.id === tripId ? mapped : t)));
        },
        onOp: (op) => {
          const current = tripsRef.current.find((t) => t.id === tripId);
          if (!current) return;
          const clocks = clocksRef.current[tripId] ?? emptyClocks();
          const result = applyOp(current, clocks, op);
          if (!result.applied) return;
          clocksRef.current = { ...clocksRef.current, [tripId]: result.clocks };
          commitTrips((prev) => prev.map((t) => (t.id === tripId ? result.trip : t)));
        },
      });
      syncClientsRef.current.set(tripId, client);
      return client;
    },
    [createSync, commitTrips],
  );

  /** Fold a batch of ops for one trip through `applyOp` (optimistic local
   *  apply), commit the resulting trip + clocks, then send each op over
   *  that trip's sync connection (queued offline, flushed on reconnect). */
  const dispatch = useCallback(
    (tripId: string, ops: Array<Op>): void => {
      if (ops.length === 0) return;
      const trip = tripsRef.current.find((t) => t.id === tripId);
      if (!trip) return;
      let nextTrip = trip;
      let clocks = clocksRef.current[tripId] ?? emptyClocks();
      for (const op of ops) {
        const result = applyOp(nextTrip, clocks, op);
        nextTrip = result.trip;
        clocks = result.clocks;
      }
      clocksRef.current = { ...clocksRef.current, [tripId]: clocks };
      const finalTrip = nextTrip;
      commitTrips((prev) => prev.map((t) => (t.id === tripId ? finalTrip : t)));
      const client = syncClientsRef.current.get(tripId);
      if (client) for (const op of ops) client.sendOp(op);
    },
    [commitTrips],
  );

  /** Convenience wrapper over `dispatch` for the common case of setting one
   *  or more fields on a single place. */
  const dispatchPlaceFields = useCallback(
    (tripId: string, placeId: string, fields: Partial<TripPlace>): void => {
      dispatch(
        tripId,
        Object.entries(fields).map(([field, value]) =>
          makeOp(tripId, 'place', 'set', { entityId: placeId, field, value }),
        ),
      );
    },
    [dispatch, makeOp],
  );

  // ── Live sync connections ────────────────────────────────────────────────
  // Every trip is server-authoritative now: as soon as a trip appears in
  // `trips` (freshly created, imported, or restored from the localStorage
  // cache on mount), open a `createTripSync` connection for it. Hydrate
  // replaces the local trip with the server's materialized state (and
  // resets its clocks — the server's field clocks supersede whatever this
  // device had guessed); incoming ops apply the same way a local optimistic
  // edit would. Sockets for trips that leave `trips` (deleted) are closed.
  useEffect(() => {
    const clients = syncClientsRef.current;
    const activeIds = new Set(trips.map((t) => t.id));
    for (const trip of trips) ensureSyncClient(trip.id);
    for (const [id, client] of clients.entries()) {
      if (!activeIds.has(id)) {
        client.close();
        clients.delete(id);
      }
    }
    return undefined;
  }, [trips, ensureSyncClient]);

  // Load the account's trips from the server on mount so they follow the user
  // across devices: add any server trip we don't have cached as a stub (its sync
  // connection hydrates the rest). Re-key the cache to this user, dropping a
  // previous account's cache. Skipped with no session (tests).
  useEffect(() => {
    if (!userId) return undefined;
    if (safeGet(TRIPS_USER_KEY) !== userId) {
      try {
        localStorage.setItem(TRIPS_USER_KEY, userId);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(KNOWN_SERVER_TRIPS_KEY);
      } catch {
        /* localStorage unavailable */
      }
    }
    let cancelled = false;
    fetch('/api/trips')
      // Only reconcile on a SUCCESSFUL fetch (online) — offline we keep the
      // cache untouched so the itinerary stays available.
      .then((response) =>
        response.ok ? (response.json() as Promise<{ trips: Array<ServerTripSummary> }>) : null,
      )
      .then((data) => {
        if (cancelled || !data) return;
        const serverTrips = data.trips ?? [];
        const serverIds = new Set(serverTrips.map((s) => s.id));
        const known = new Set(loadKnownServerTrips());
        commitTrips((prev) => {
          // Keep trips the server still lists, plus any never-confirmed
          // (pending-create) trips; drop ones the server knew before but no
          // longer lists (deleted / unshared on another device).
          const kept = prev.filter((t) => serverIds.has(t.id) || !known.has(t.id));
          const keptIds = new Set(kept.map((t) => t.id));
          const additions = serverTrips.filter((s) => !keptIds.has(s.id)).map(stubFromSummary);
          return [...kept, ...additions];
        });
        saveKnownServerTrips([...serverIds]);
      })
      .catch(() => {
        /* offline / error — keep the cache as-is */
      });
    return () => {
      cancelled = true;
    };
  }, [userId, commitTrips]);

  // Close every open sync connection on unmount.
  useEffect(() => {
    const clients = syncClientsRef.current;
    return () => {
      for (const client of clients.values()) client.close();
      clients.clear();
    };
  }, []);

  const createTrip = useCallback(
    (
      name: string,
      location: string,
      startDate: string,
      endDate: string,
      lat?: number | null,
      lng?: number | null,
      radius?: number | null,
    ): Trip => {
      const id = crypto.randomUUID();
      const trimmedName = name.trim() || 'My Trip';
      const trip: Trip = {
        id,
        name: trimmedName,
        location,
        lat,
        lng,
        radius,
        startDate,
        endDate,
        createdAt: Date.now(),
        places: [],
      };
      clocksRef.current = { ...clocksRef.current, [id]: emptyClocks() };
      commitTrips((prev) => [...prev, trip]);
      // Open the sync connection synchronously — the dispatch below (in
      // the same call, well before the sync-connection effect would
      // otherwise run) needs a client to send the trip's first ops to.
      ensureSyncClient(id);

      // The first op for a fresh trip id auto-creates the server row.
      const ops: Array<Op> = [
        makeOp(id, 'trip', 'set', { field: 'name', value: trimmedName }),
        makeOp(id, 'trip', 'set', { field: 'location', value: location }),
      ];
      if (lat !== undefined) ops.push(makeOp(id, 'trip', 'set', { field: 'lat', value: lat }));
      if (lng !== undefined) ops.push(makeOp(id, 'trip', 'set', { field: 'lng', value: lng }));
      if (radius !== undefined) {
        ops.push(makeOp(id, 'trip', 'set', { field: 'radius', value: radius }));
      }
      ops.push(makeOp(id, 'trip', 'set', { field: 'startDate', value: startDate }));
      ops.push(makeOp(id, 'trip', 'set', { field: 'endDate', value: endDate }));
      dispatch(id, ops);

      return trip;
    },
    [commitTrips, dispatch, makeOp, ensureSyncClient],
  );

  const deleteTrip = useCallback(
    (id: string): void => {
      // Archive the trip's Immich album (if any) before we lose the
      // album-id reference. Fire-and-forget — the worker call can fail
      // (no IMMICH config, network blip, etc.) without blocking the
      // local trip-delete. The album stays in the user's library; only
      // the name + description change to mark it as no longer linked.
      const trip = tripsRef.current.find((t) => t.id === id);
      if (trip?.immichAlbumId) {
        void archiveTripAlbum({
          albumId: trip.immichAlbumId,
          originalAlbumName: trip.name,
          tripLocation: trip.location,
        }).catch((err) => {
          console.warn('[trip] archiveTripAlbum failed', err);
        });
      }
      const client = syncClientsRef.current.get(id);
      if (client) {
        client.close();
        syncClientsRef.current.delete(id);
      }
      const { [id]: _removedClocks, ...remainingClocks } = clocksRef.current;
      clocksRef.current = remainingClocks;
      // Delete server-side too so it leaves the account's trip list (owner
      // tombstones the trip; a collaborator just leaves). Fire-and-forget — the
      // local removal happens regardless of network.
      void fetch(`/api/trips/${encodeURIComponent(id)}`, { method: 'DELETE' }).catch(() => {});
      commitTrips((prev) => prev.filter((t) => t.id !== id));
    },
    [commitTrips],
  );

  const addPlace = useCallback(
    (tripId: string, item: ItineraryItem, placeType: TripPlaceType): void => {
      // First-place celebration: if this trip has zero places + the
      // user hasn't seen the celebration for this trip yet, dispatch a
      // window event so any listener (typically App) can fire a small
      // confetti burst after the add commits. Gated by a per-trip
      // localStorage flag so it only fires once. We detect BEFORE the
      // dispatch (reading from the latest ref) so we have the
      // pre-add state available.
      const trip = tripsRef.current.find((t) => t.id === tripId);
      const isFirstPlace =
        trip != null && trip.places.length === 0 && !trip.places.some((p) => p.item.id === item.id);
      if (isFirstPlace) {
        const flagKey = `trip-explorer:first-place-celebrated:${tripId}`;
        try {
          if (!localStorage.getItem(flagKey)) {
            localStorage.setItem(flagKey, '1');
            // Fire after commit so the new row is mounted when the
            // confetti starts — the burst visually originates from
            // the place that just appeared.
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent('trippy-first-place-added', { detail: { tripId } }),
              );
            }, 80);
          }
        } catch {
          // localStorage unavailable (private browsing, quota) — skip
          // the gate and just fire so the user still gets the moment.
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('trippy-first-place-added', { detail: { tripId } }),
            );
          }, 80);
        }
      }
      dispatch(tripId, [
        makeOp(tripId, 'place', 'add', { entityId: item.id, value: { item, placeType } }),
      ]);
    },
    [dispatch, makeOp],
  );

  const removePlace = useCallback(
    (tripId: string, placeId: string): void => {
      dispatch(tripId, [makeOp(tripId, 'place', 'remove', { entityId: placeId })]);
    },
    [dispatch, makeOp],
  );

  const schedulePlace = useCallback(
    (
      tripId: string,
      placeId: string,
      day: number,
      dayEnd?: number | null,
      time?: string | null,
    ): void =>
      dispatchPlaceFields(tripId, placeId, {
        scheduledDay: day,
        scheduledDayEnd: dayEnd ?? null,
        scheduledTime: time ?? null,
      }),
    [dispatchPlaceFields],
  );

  const updatePlaceSchedule = useCallback(
    (
      tripId: string,
      placeId: string,
      update: Pick<
        TripPlace,
        | 'scheduledDay'
        | 'scheduledDayEnd'
        | 'scheduledTime'
        | 'estimatedDurationMinutes'
        | 'checkInTime'
        | 'checkInEndTime'
        | 'checkOutTime'
        | 'checkOutEndTime'
      >,
    ): void => dispatchPlaceFields(tripId, placeId, update),
    [dispatchPlaceFields],
  );

  const unschedulePlace = useCallback(
    (tripId: string, placeId: string): void =>
      dispatchPlaceFields(tripId, placeId, {
        scheduledDay: null,
        scheduledDayEnd: null,
        scheduledTime: null,
      }),
    [dispatchPlaceFields],
  );

  const updatePlaceType = useCallback(
    (tripId: string, placeId: string, placeType: TripPlaceType): void =>
      // Changing the itinerary type unschedules the place — different types have different
      // scheduling semantics (lodging spans days, activities have times) so the existing
      // schedule may no longer make sense.
      dispatchPlaceFields(tripId, placeId, {
        placeType,
        scheduledDay: null,
        scheduledDayEnd: null,
        scheduledTime: null,
      }),
    [dispatchPlaceFields],
  );

  const updatePlaceNote = useCallback(
    (tripId: string, placeId: string, note: string | null): void =>
      dispatchPlaceFields(tripId, placeId, { note: note || null }),
    [dispatchPlaceFields],
  );

  // Reading-only helpers: read from ref so the function reference is stable.
  // Callers that need to react to trip changes will re-render anyway when
  // `trips` itself changes (it's in the context value), at which point a
  // re-invocation reads the updated state via the ref.
  const isInTrip = useCallback(
    (tripId: string, placeId: string): boolean =>
      tripsRef.current.find((t) => t.id === tripId)?.places.some((p) => p.item.id === placeId) ??
      false,
    [],
  );

  const isInAnyTrip = useCallback(
    (placeId: string): boolean =>
      tripsRef.current.some((t) => t.places.some((p) => p.item.id === placeId)),
    [],
  );

  const exportTrip = useCallback((tripId: string): void => {
    const trip = tripsRef.current.find((t) => t.id === tripId);
    if (!trip) return;
    // Strip live-sync identity so an exported JSON, when re-imported,
    // doesn't try to enroll the importer as a second owner of the
    // original's Collaborate channel. Also strip the Immich album +
    // photo refs (trip-level AND per-place) — those point at the
    // original user's Immich library; an importer wouldn't have access
    // to those asset IDs anyway. Collaborators are stripped for the
    // same reason: they're permissions on the original's Immich album,
    // not portable membership.
    const {
      immichAlbumId: _immichAlbumId,
      immichPhotos: _immichPhotos,
      collaborators: _collaborators,
      places,
      ...rest
    } = trip;
    const exportable = {
      ...rest,
      places: places.map(({ immichPhotos: _ip, ...placeRest }) => placeRest),
    };
    const blob = new Blob([JSON.stringify(exportable, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${trip.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, []);

  const updateTripLocation = useCallback(
    (tripId: string, location: string, lat: number, lng: number): void => {
      dispatch(tripId, [
        makeOp(tripId, 'trip', 'set', { field: 'location', value: location }),
        makeOp(tripId, 'trip', 'set', { field: 'lat', value: lat }),
        makeOp(tripId, 'trip', 'set', { field: 'lng', value: lng }),
      ]);
    },
    [dispatch, makeOp],
  );

  const updateTripDates = useCallback(
    (tripId: string, startDate: string, endDate: string): void => {
      const trip = tripsRef.current.find((t) => t.id === tripId);
      if (!trip) return;
      const newDayCount = Math.max(
        1,
        Math.round(
          (new Date(`${endDate}T00:00:00`).getTime() -
            new Date(`${startDate}T00:00:00`).getTime()) /
            86_400_000,
        ) + 1,
      );
      const ops: Array<Op> = [
        makeOp(tripId, 'trip', 'set', { field: 'startDate', value: startDate }),
        makeOp(tripId, 'trip', 'set', { field: 'endDate', value: endDate }),
      ];
      // Places that fall outside the new range get clamped alongside the
      // date change, in the same dispatch — a peer applying this batch sees
      // the new dates and the clamp atomically.
      for (const place of trip.places) {
        const placeId = place.item.id;
        if (place.scheduledDay != null && place.scheduledDay > newDayCount) {
          ops.push(
            makeOp(tripId, 'place', 'set', {
              entityId: placeId,
              field: 'scheduledDay',
              value: null,
            }),
            makeOp(tripId, 'place', 'set', {
              entityId: placeId,
              field: 'scheduledDayEnd',
              value: null,
            }),
            makeOp(tripId, 'place', 'set', {
              entityId: placeId,
              field: 'scheduledTime',
              value: null,
            }),
          );
        } else if (place.scheduledDayEnd != null && place.scheduledDayEnd > newDayCount) {
          ops.push(
            makeOp(tripId, 'place', 'set', {
              entityId: placeId,
              field: 'scheduledDayEnd',
              value: newDayCount,
            }),
          );
        }
      }
      dispatch(tripId, ops);
    },
    [dispatch, makeOp],
  );

  const importTrip = useCallback(
    (data: unknown): Trip | null => {
      try {
        const raw = data as Trip;
        if (!(raw.name && Array.isArray(raw.places))) return null;
        // An imported copy is a standalone trip: assign it a fresh id (which
        // becomes its own sync channel) and strip the source's Immich album +
        // photo refs — the importer doesn't have access to the original user's
        // Immich library, so leaving those would render as broken thumbnails.
        const {
          immichAlbumId: _immichAlbumId,
          immichPhotos: _immichPhotos,
          collaborators: _collaborators,
          places: rawPlaces,
          ...rawRest
        } = raw;
        const cleanedPlaces = rawPlaces.map(({ immichPhotos: _ip, ...placeRest }) => placeRest);
        const imported: Trip = backfillTrip({
          ...rawRest,
          places: cleanedPlaces,
          id: crypto.randomUUID(),
          createdAt: Date.now(),
        });
        clocksRef.current = { ...clocksRef.current, [imported.id]: emptyClocks() };
        commitTrips((prev) => [...prev, imported]);
        ensureSyncClient(imported.id);
        return imported;
      } catch {
        return null;
      }
    },
    [commitTrips, ensureSyncClient],
  );

  // ── Live trip sync ───────────────────────────────────────────────────────
  // Every trip is server-synced unconditionally (see the sync-connection
  // effect above) — there's no separate "enable" step. The sync channel id IS
  // the trip id: every device that holds this trip uses the same `trip.id`
  // locally, so a `?live=<id>` link resolves to the same trip everywhere.

  // In-flight import promises keyed by syncId. Without this dedup,
  // React strict mode (which runs effects twice in dev) and any
  // accidental double-call would race past the `tripsRef.current`
  // dedup check below — both calls fetch + commit before either's
  // commit lands in the ref, producing two local copies of the same
  // live trip.
  const importInFlightRef = useRef<Map<string, Promise<Trip | null>>>(new Map());

  const importLiveSyncedTrip = useCallback(
    async (syncId: string): Promise<Trip | null> => {
      // Idempotent — if this device already has a copy of this trip, return it
      // (keyed on `t.id`, which is the sync id). Also catches the owner clicking
      // their own share link.
      const existing = tripsRef.current.find((t) => t.id === syncId);
      if (existing) return existing;
      // If another caller is already mid-import for this syncId,
      // piggyback on that promise instead of starting a second fetch.
      const inFlight = importInFlightRef.current.get(syncId);
      if (inFlight) return inFlight;
      const promise = (async (): Promise<Trip | null> => {
        const response = await fetch(`${API_BASE}/api/trips/${syncId}/state`);
        if (!response.ok) return null;
        const data = (await response.json()) as { trip: HydratedTrip | null };
        if (!data.trip) return null;
        // Double-check the dedup AFTER the network round-trip — a
        // concurrent strict-mode invocation may have committed its
        // copy while we were awaiting the response.
        const racedExisting = tripsRef.current.find((t) => t.id === syncId);
        if (racedExisting) return racedExisting;
        const newTrip: Trip = tripStateToTrip(data.trip);
        clocksRef.current = { ...clocksRef.current, [syncId]: emptyClocks() };
        commitTrips((prev) => [...prev, newTrip]);
        ensureSyncClient(syncId);
        return newTrip;
      })();
      importInFlightRef.current.set(syncId, promise);
      // Clear the in-flight entry once settled so a later legitimate
      // re-import (e.g. user removed the local copy and revisited the
      // link) can succeed.
      void promise.finally(() => {
        if (importInFlightRef.current.get(syncId) === promise) {
          importInFlightRef.current.delete(syncId);
        }
      });
      return promise;
    },
    [commitTrips, ensureSyncClient],
  );

  const addEnRouteNote = useCallback(
    (tripId: string, text: string, direction: EnRouteDirection): void => {
      const id = crypto.randomUUID();
      dispatch(tripId, [
        makeOp(tripId, 'enroute', 'add', { entityId: id, value: { id, text, direction } }),
      ]);
    },
    [dispatch, makeOp],
  );

  const updateEnRouteNote = useCallback(
    (tripId: string, noteId: string, text: string): void => {
      dispatch(tripId, [
        makeOp(tripId, 'enroute', 'set', { entityId: noteId, field: 'text', value: text }),
      ]);
    },
    [dispatch, makeOp],
  );

  const removeEnRouteNote = useCallback(
    (tripId: string, noteId: string): void => {
      dispatch(tripId, [makeOp(tripId, 'enroute', 'remove', { entityId: noteId })]);
    },
    [dispatch, makeOp],
  );

  // ── Immich photo plumbing ────────────────────────────────────────────────
  // The actual upload happens in the UI layer (`uploadPhotoToImmich` in
  // `@/api/immich`). These mutators persist the result and own the
  // "remove from album" side-effect on unlink.

  const attachPhotoToPlace = useCallback(
    (tripId: string, placeId: string, photo: ImmichPhotoRef): void => {
      const trip = tripsRef.current.find((t) => t.id === tripId);
      const place = trip?.places.find((p) => p.item.id === placeId);
      if (!place) return;
      dispatchPlaceFields(tripId, placeId, {
        immichPhotos: [...(place.immichPhotos ?? []), photo],
      });
    },
    [dispatchPlaceFields],
  );

  const removePhotoFromPlace = useCallback(
    (tripId: string, placeId: string, assetId: string): void => {
      // Fire the worker call to drop the album-membership before we
      // forget the album id. The local commit still happens regardless
      // of whether the worker call succeeds — the user's intent is
      // "remove from my trip"; the album side-effect is best-effort.
      const trip = tripsRef.current.find((t) => t.id === tripId);
      if (trip?.immichAlbumId) {
        void removePhotosFromAlbum(trip.immichAlbumId, [assetId]).catch((err) => {
          console.warn('[trip] removePhotosFromAlbum failed', err);
        });
      }
      const place = trip?.places.find((p) => p.item.id === placeId);
      if (!place) return;
      dispatchPlaceFields(tripId, placeId, {
        immichPhotos: (place.immichPhotos ?? []).filter((ph) => ph.assetId !== assetId),
      });
    },
    [dispatchPlaceFields],
  );

  const attachPhotoToTrip = useCallback(
    (tripId: string, photo: ImmichPhotoRef): void => {
      const trip = tripsRef.current.find((t) => t.id === tripId);
      if (!trip) return;
      dispatch(tripId, [
        makeOp(tripId, 'trip', 'set', {
          field: 'immichPhotos',
          value: [...(trip.immichPhotos ?? []), photo],
        }),
      ]);
    },
    [dispatch, makeOp],
  );

  const removePhotoFromTrip = useCallback(
    (tripId: string, assetId: string): void => {
      const trip = tripsRef.current.find((t) => t.id === tripId);
      if (!trip) return;
      if (trip.immichAlbumId) {
        void removePhotosFromAlbum(trip.immichAlbumId, [assetId]).catch((err) => {
          console.warn('[trip] removePhotosFromAlbum failed', err);
        });
      }
      dispatch(tripId, [
        makeOp(tripId, 'trip', 'set', {
          field: 'immichPhotos',
          value: (trip.immichPhotos ?? []).filter((ph) => ph.assetId !== assetId),
        }),
      ]);
    },
    [dispatch, makeOp],
  );

  const addCollaborator = useCallback(
    (tripId: string, collaborator: TripCollaborator): void => {
      // Idempotent — skip if this email is already in the list. Collaborators
      // are keyed by (lowercased) email — the same key `authorizeTrip` checks —
      // so trip access doesn't depend on the person having an Immich account.
      const trip = tripsRef.current.find((t) => t.id === tripId);
      const list = trip?.collaborators ?? [];
      if (list.some((c) => c.email.toLowerCase() === collaborator.email.toLowerCase())) return;
      dispatch(tripId, [
        makeOp(tripId, 'collaborator', 'add', {
          entityId: collaborator.email.toLowerCase(),
          value: collaborator,
        }),
      ]);
    },
    [dispatch, makeOp],
  );

  const removeCollaborator = useCallback(
    (tripId: string, email: string): void => {
      // Best-effort revoke of the person's Immich album access (only if they
      // had one) before we lose the album-id reference. Local removal happens
      // regardless — the local list is the source of truth.
      const trip = tripsRef.current.find((t) => t.id === tripId);
      const collaborator = trip?.collaborators?.find(
        (c) => c.email.toLowerCase() === email.toLowerCase(),
      );
      if (trip?.immichAlbumId && collaborator?.immichUserId) {
        void removeAlbumCollaborator(trip.immichAlbumId, collaborator.immichUserId).catch((err) => {
          console.warn('[trip] removeAlbumCollaborator failed', err);
        });
      }
      dispatch(tripId, [
        makeOp(tripId, 'collaborator', 'remove', { entityId: email.toLowerCase() }),
      ]);
    },
    [dispatch, makeOp],
  );

  const setTripImmichAlbumId = useCallback(
    (tripId: string, albumId: string): void => {
      dispatch(tripId, [makeOp(tripId, 'trip', 'set', { field: 'immichAlbumId', value: albumId })]);
    },
    [dispatch, makeOp],
  );

  // Memoize the result so the context value changes identity only when `trips`
  // itself changes. All handler references are stable across renders, so
  // consumers of useTripContext() that only use handlers won't re-render on
  // unrelated state changes.
  return useMemo(
    () => ({
      trips,
      createTrip,
      deleteTrip,
      addPlace,
      removePlace,
      schedulePlace,
      updatePlaceSchedule,
      unschedulePlace,
      updatePlaceType,
      updatePlaceNote,
      isInTrip,
      isInAnyTrip,
      exportTrip,
      importTrip,
      updateTripLocation,
      updateTripDates,
      addEnRouteNote,
      updateEnRouteNote,
      removeEnRouteNote,
      importLiveSyncedTrip,
      attachPhotoToPlace,
      removePhotoFromPlace,
      attachPhotoToTrip,
      removePhotoFromTrip,
      setTripImmichAlbumId,
      addCollaborator,
      removeCollaborator,
    }),
    [
      trips,
      createTrip,
      deleteTrip,
      addPlace,
      removePlace,
      schedulePlace,
      updatePlaceSchedule,
      unschedulePlace,
      updatePlaceType,
      updatePlaceNote,
      isInTrip,
      isInAnyTrip,
      exportTrip,
      importTrip,
      updateTripLocation,
      updateTripDates,
      addEnRouteNote,
      updateEnRouteNote,
      removeEnRouteNote,
      importLiveSyncedTrip,
      attachPhotoToPlace,
      removePhotoFromPlace,
      attachPhotoToTrip,
      removePhotoFromTrip,
      setTripImmichAlbumId,
      addCollaborator,
      removeCollaborator,
    ],
  );
};
